// src/services/googleSheetsService.js

import googleApiClient from "./googleApiClient";

/**
 * Reads data from a given Google Sheet and range.
 * @param {string} spreadsheetId - The ID of the target Google Sheet.
 * @param {string} range - A1 notation range, e.g., 'Books!A1:D20'
 * @returns {Promise<Array>} 2D array of values
 */
export const readSheet = async (spreadsheetId, range) => {
  try {
    const response = await googleApiClient.get(
      `/spreadsheets/${spreadsheetId}/values/${range}`
    );
    return response.data.values || [];
  } catch (error) {
    console.error("Error reading sheet:", error);
    throw error;
  }
};

/**
 * Appends a row to a given Google Sheet.
 * @param {string} spreadsheetId - The ID of the target Google Sheet.
 * @param {string} range - A1 notation range, e.g., 'Orders!A:D'
 * @param {Array} rowData - Array of cell values
 * @returns {Promise<Object>} The API response
 */
export const appendRow = async (spreadsheetId, range, rowData) => {
  try {
    const response = await googleApiClient.post(
      `/spreadsheets/${spreadsheetId}/values/${range}:append`,
      {
        values: [rowData],
      },
      {
        params: {
          valueInputOption: "USER_ENTERED",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error appending row:", error);
    throw error;
  }
};

/**
 * Updates a range in a Google Sheet.
 * @param {string} spreadsheetId - The ID of the target Google Sheet.
 * @param {string} range - A1 notation range.
 * @param {Array<Array>} values - 2D array of values to write.
 * @returns {Promise<Object>} The API response
 */
export const updateSheet = async (spreadsheetId, range, values) => {
  try {
    const response = await googleApiClient.put(
      `/spreadsheets/${spreadsheetId}/values/${range}`,
      { values },
      {
        params: {
          valueInputOption: "USER_ENTERED",
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating sheet:", error);
    throw error;
  }
};

// Service object for convenience
export const googleSheetsService = {
  readSheet,
  appendRow,
  updateSheet,
};